// ============================================================================
// HR module — read-only data hooks. Plain supabase.from() + useState/useEffect,
// matching the rest of the codebase (account-management, hr-hiring, etc.).
// No TanStack Query.
//
// RLS gates every query: the hr / executive / superuser role sees everything;
// AMs are blocked at the route layer (see HrGuard) but RLS would also return
// 0 rows for non-HR roles anyway.
// ============================================================================
import { useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { catalogLabel } from '../../../shared/service-roles';

// ---------------------------------------------------------------------------
// Dashboard tile counts.
// ---------------------------------------------------------------------------

// useHrDashboardCounts and its HrDashboardCounts type were removed 2026-10-08
// with the HR dashboard (item 818): the Desk reads each system of record
// through its own screen's hook instead of a second set of counts.

// Statuses that mean "off the default roster".
//
// ⛔ 'Archived' WAS RETIRED 2026-08-26. It was a soft-delete value set by an
// Archive button, and nothing ever distinguished it from 'Former' — three of
// the seven off-roster people had a NULL separation date and a NULL reason
// under it. The three rows carrying it were migrated to Former and the write
// path is gone; employees.employment_status now CHECKs Active or Former.
//
// The list below stays WIDE on purpose for one release: reads must keep
// tolerating the retired values so a cached page or an in-flight query does not
// suddenly file somebody under the live roster. Nothing WRITES them any more.
export const EMPLOYEE_INACTIVE_STATUSES = ['Terminated', 'Separated', 'Archived', 'Former'];
export const EMPLOYEE_FORMER_STATUS = 'Former';

// ⛔ useFullEmployees and useW2HourlyEmployees were DELETED 2026-08-25.
// Both required employment_type='W2' and differed only on an
// @terminalcleaning.com email test, so between them they missed every employee
// who was not W2 — two Subcontractors were invisible and a 1099 would have
// been. Their screens are gone and useDirectoryEmployees below replaces both
// with no class filter at all. Deleted rather than left unused: an unused hook
// that encodes a known-wrong rule is the next person's shortcut.
// The HourlyEmployeeRow / FullEmployeeRow types went with them.

// Patch an employee row. hr + superuser have UPDATE via RLS; other roles get a
// PostgREST/RLS error which the caller surfaces. Used by the edit modal and by
// archive/restore (which just set employment_status). H1 (Antonela 2026-06-02).
export async function updateEmployee(
  employeeId: string,
  patch: Record<string, unknown>,
): Promise<void> {
  const { error } = await supabase
    .from('employees')
    .update(patch)
    .eq('employee_id', employeeId);
  if (error) throw error;
}

// Create a new employees row. hr + superuser have INSERT via RLS (hr_write /
// superuser_all); other roles get an RLS error which the caller surfaces.
//
// THE ID IS MINTED BY THE DATABASE, NOT HERE. trg_employees_assign_id calls
// next_prefixed_id('EID') on insert, the same counter every other TCS entity
// uses. Do NOT reintroduce a client-side id: this function used to read every
// employee_id, take the numeric max, and insert max+1. That is the pattern that
// produced two Joshua Lopez records (EID-0036 and EID-0037, entered two days
// apart) and it cannot see a concurrent insert, so two people onboarding at
// once could compute the same number. The DB counter is atomic and cannot.
//
// Ben's ruling 2026-09-02: EIDs are always NEXT-HIGHER and gaps are NEVER
// backfilled. The counter enforces that on its own — nine numbers are unused
// (EID-0004, 0006-0008, 0011-0014, 0036) and must stay unused, because
// audit_log still attributes real financial rows and a deletion to some of
// them. Do not "tidy" the sequence by reusing a hole.
//
// employee_id is omitted from the insert entirely so the trigger fires; it is
// then read back from the RETURNING row. Returns the new employee_id.
export async function createEmployee(
  fields: Record<string, unknown>,
): Promise<string> {
  // Strip any caller-supplied employee_id — passing one would silently bypass
  // the trigger, which is exactly the bug this function no longer has.
  const { employee_id: _ignored, ...safeFields } = fields;
  const { data, error } = await supabase
    .from('employees')
    .insert(safeFields)
    .select('employee_id')
    .single();
  if (error) throw error;
  const newId = (data as { employee_id: string } | null)?.employee_id;
  if (!newId) {
    throw new Error(
      'createEmployee: the database did not return an employee_id. The insert may have succeeded without one — check trg_employees_assign_id before retrying.',
    );
  }
  return newId;
}

// Patch a partner-sourced worker on its service_assignments row (HR edit of a
// worker card). HR has UPDATE via RLS (hr_update) and bypasses the
// protected-cols guard, so name/phone/training fields are editable here. The
// guard still blocks rate/provider/status churn for non-HR roles.
export async function updateAssignmentWorker(
  assignmentId: string,
  patch: Record<string, unknown>,
): Promise<void> {
  const { error } = await supabase
    .from('service_assignments')
    .update(patch)
    .eq('assignment_id', assignmentId);
  if (error) throw error;
}

// Conversion: a W2-hourly TCS worker is hired DIRECTLY by the client (hospital),
// coming off TCS payroll. Records an employee_separations row (type
// 'Conversion') with the converted-to account/facility, applies the flat
// $1,450 fee when the hire falls within 6 months of the TCS start date,
// auto-creates the billing_adjustments line for that fee, and flips the
// employee to Former. All of that happens inside the SECURITY DEFINER
// convert_employee_to_client_hire RPC. Returns { separation_id, conversion_fee,
// within_window, billing_adjustment_id }.
export interface ConversionResult {
  separation_id: string;
  conversion_fee: number;
  within_window: boolean;
  billing_adjustment_id: string | null;
}

export async function convertEmployeeToClientHire(input: {
  employeeId: string;
  accountId?: string | null;
  facilityId?: string | null;
  conversionDate?: string | null;
  startDateOverride?: string | null;
  autoBill?: boolean;
}): Promise<ConversionResult> {
  const { data, error } = await supabase.rpc('convert_employee_to_client_hire', {
    p_employee_id: input.employeeId,
    p_converted_to_account_id: input.accountId ?? null,
    p_converted_to_facility_id: input.facilityId ?? null,
    p_conversion_date: input.conversionDate ?? null,
    p_start_date_override: input.startDateOverride ?? null,
    p_auto_bill: input.autoBill ?? true,
  });
  if (error) throw error;
  return data as ConversionResult;
}

// ---------------------------------------------------------------------------
// Partner-sourced workers — service_assignments WHERE worker_name IS NOT NULL.
// ---------------------------------------------------------------------------

export interface PartnerWorkerRow {
  assignment_id: string;
  worker_name: string | null;
  worker_phone: string | null;
  provider_id: string | null;
  facility_id: string | null;
  service_code: string | null;
  service_label: string | null;
  assignment_status: string | null;
  training_status: string | null;
  training_completion_date: string | null;
  facility_access_issued: string | null;
  provider_name?: string | null;
  facility_name?: string | null;
}

export function usePartnerWorkers() {
  const [data, setData] = useState<PartnerWorkerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const refetch = () => setTick((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      // H4 (Antonela 2026-06-02): an hourly/sub staffing card must lead with the
      // PERSON. We no longer require worker_name — we surface every active
      // assignment so the ~20 rows still awaiting a worker name show up with a
      // "(name pending)" tag (those names are being backfilled separately).
      const res = await supabase
        .from('service_assignments')
        .select(
          'assignment_id, worker_name, worker_phone, provider_id, facility_id, service_code, service_catalog(display_name), assignment_status, training_status, training_completion_date, facility_access_issued',
        )
        .order('worker_name', { ascending: true, nullsFirst: false });
      if (cancelled) return;
      if (res.error) {
        setError(res.error.message);
        setData([]);
        setLoading(false);
        return;
      }
      // Keep only staffing that's worth showing: a named worker, or an active
      // assignment whose person name is still pending. Ended/Inactive/Terminated
      // rows with no name are noise and stay hidden.
      const rows = ((res.data ?? []) as unknown as Array<
        Omit<PartnerWorkerRow, 'service_label'> & {
          service_catalog: { display_name: string | null }[] | { display_name: string | null } | null;
        }
      >)
        .map((r) => ({ ...r, service_label: catalogLabel(r.service_catalog) }))
        .filter((r) => !!r.worker_name || r.assignment_status === 'Active');
      const provIds = Array.from(
        new Set(rows.map((r) => r.provider_id).filter((x): x is string => !!x)),
      );
      const facIds = Array.from(
        new Set(rows.map((r) => r.facility_id).filter((x): x is string => !!x)),
      );
      const [provRes, facRes] = await Promise.all([
        provIds.length
          ? supabase
              .from('service_providers')
              .select('provider_id, partner_name')
              .in('provider_id', provIds)
          : Promise.resolve({ data: [], error: null } as const),
        facIds.length
          ? supabase
              .from('facilities')
              .select('facility_id, facility_name')
              .in('facility_id', facIds)
          : Promise.resolve({ data: [], error: null } as const),
      ]);
      const provMap = Object.fromEntries(
        ((provRes.data ?? []) as { provider_id: string; partner_name: string | null }[]).map(
          (p) => [p.provider_id, p.partner_name],
        ),
      );
      const facMap = Object.fromEntries(
        ((facRes.data ?? []) as { facility_id: string; facility_name: string | null }[]).map(
          (f) => [f.facility_id, f.facility_name],
        ),
      );
      setData(
        rows.map((r) => ({
          ...r,
          provider_name: r.provider_id ? provMap[r.provider_id] ?? null : null,
          facility_name: r.facility_id ? facMap[r.facility_id] ?? null : null,
        })),
      );
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  return { data, loading, error, refetch };
}

// ---------------------------------------------------------------------------
// ⛔ usePartnerOnboarding() and useSubProviders() USED TO LIVE HERE.
//
// Their only callers were src/modules/hr/onboarding.tsx and
// src/modules/hr/people-subs.tsx, which were exported from the HR barrel and
// routed nowhere — /hr/onboarding and /hr/people/subs are <Navigate> redirects
// to the hr-onboarding pages. All four were tree-shaken out of the production
// bundle, so this code had no effect on anything a person could open. Deleted
// 2026-08-24 (Ben's call).
//
// The live equivalents are useOnboardingRecords() and useSubcontractors() in
// src/modules/hr-onboarding/hooks/use-onboarding-data.ts, which already read
// COI state from partner_documents (TCSD-0104).
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Single employee detail (for /hr/people/full/:employee_id).
// ---------------------------------------------------------------------------

export function useEmployeeDetail(employeeId: string | undefined) {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const refetch = () => setTick((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!employeeId) {
        setData(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      const res = await supabase
        .from('employees')
        .select('*')
        .eq('employee_id', employeeId)
        .maybeSingle();
      if (cancelled) return;
      if (res.error) {
        setError(res.error.message);
        setData(null);
      } else {
        setData((res.data ?? null) as Record<string, unknown> | null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [employeeId, tick]);

  return { data, loading, error, refetch };
}

// ---------------------------------------------------------------------------
// The Employees directory — EVERY employee, in one query.
//
// ⛔ NO `employment_type` FILTER, and that is the whole point. The two hooks
// this replaced (useFullEmployees, useW2HourlyEmployees — deleted 2026-08-25)
// both required employment_type='W2' and were complementary only on the email
// test, so anybody who was not W2 appeared in NEITHER. The two `Subcontractor`
// employees were already invisible and a `1099` employee would have been too —
// which is what Antonela reported. Grouping is by `pay_type` (TCSD-0117) and
// happens in ../people-directory, which is pure and tested.
// ---------------------------------------------------------------------------

export interface DirectoryEmployeeRow {
  employee_id: string;
  employee_name_first: string | null;
  employee_name_last: string | null;
  employee_title: string | null;
  pay_type: string | null;
  employment_type: string | null;
  employment_status: string | null;
  primary_facility_id: string | null;
  work_state: string | null;
  work_state_source: string | null;
  email_work: string | null;
  email_personal: string | null;
  hire_date: string | null;
  dob: string | null;
  app_role: string | null;
  current_shift: string | null;
  photo_url: string | null;
  facility_name?: string | null;
}

export interface CurrentCompRow {
  employee_id: string;
  wage_rate: number | null;
  pay_frequency: string | null;
  standard_hours: number | null;
  effective_date: string | null;
}

const DIRECTORY_COLUMNS =
  'employee_id, employee_name_first, employee_name_last, employee_title, pay_type, '
  + 'employment_type, employment_status, primary_facility_id, work_state, work_state_source, '
  + 'email_work, email_personal, hire_date, dob, app_role, current_shift, photo_url';

export function useDirectoryEmployees() {
  const [data, setData] = useState<DirectoryEmployeeRow[]>([]);
  const [comp, setComp] = useState<Record<string, CurrentCompRow>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const refetch = () => setTick((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const res = await supabase
        .from('employees')
        .select(DIRECTORY_COLUMNS)
        .order('employee_name_first', { ascending: true });
      if (cancelled) return;
      if (res.error) {
        setError(res.error.message);
        setData([]);
        setLoading(false);
        return;
      }
      const rows = (res.data ?? []) as unknown as DirectoryEmployeeRow[];

      // Facility names: flat-fetch + IN-lookup + client join. `primary_facility_id`
      // carries no FK, so a PostgREST embed would fail as a permissions error
      // (TCSD-0086) rather than as a missing relationship.
      const facIds = Array.from(
        new Set(rows.map((r) => r.primary_facility_id).filter((x): x is string => !!x)),
      );
      let facMap: Record<string, string | null> = {};
      if (facIds.length > 0) {
        const facRes = await supabase
          .from('facilities')
          .select('facility_id, facility_name')
          .in('facility_id', facIds);
        if (!facRes.error && facRes.data) {
          facMap = Object.fromEntries(
            (facRes.data as { facility_id: string; facility_name: string | null }[]).map((f) => [
              f.facility_id,
              f.facility_name,
            ]),
          );
        }
      }

      // Current compensation. HR-only by RLS on employee_compensation, reached
      // through a security_invoker view — so a non-HR reader gets zero rows
      // rather than an error, and the page still renders with the rate column
      // reading "—". A failure here must NOT blank the directory: knowing who
      // works here matters more than knowing what they are paid.
      const compRes = await supabase
        .from('v_employee_compensation_current')
        .select('employee_id, wage_rate, pay_frequency, standard_hours, effective_date');
      const compMap: Record<string, CurrentCompRow> = {};
      if (!compRes.error && compRes.data) {
        for (const c of compRes.data as CurrentCompRow[]) compMap[c.employee_id] = c;
      }

      if (cancelled) return;
      setData(
        rows.map((r) => ({
          ...r,
          facility_name: r.primary_facility_id ? facMap[r.primary_facility_id] ?? null : null,
        })),
      );
      setComp(compMap);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  return { data, comp, loading, error, refetch };
}

// ---------------------------------------------------------------------------
// One employee's benefits elections, for the card's Benefits section.
//
// employee_benefits joins benefits_plans for the plan name and type. Both FKs
// were added 2026-08-25 (TCSD-0117) — before that the table had none at all and
// this embed would have failed as a permissions error rather than a missing
// relationship (TCSD-0086). `termination_date IS NULL` is the current election;
// supersession is explicit, so history is not lost by asking for the current row.
// ---------------------------------------------------------------------------

export interface BenefitElectionRow {
  enrollment_id: string;
  employee_id: string;
  plan_id: string | null;
  enrollment_status: string | null;
  enrollment_date: string | null;
  effective_date: string | null;
  termination_date: string | null;
  source_envelope_id: string | null;
  plan_name?: string | null;
  plan_type?: string | null;
}

export function useEmployeeBenefits(employeeId: string | undefined) {
  const [data, setData] = useState<BenefitElectionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const refetch = () => setTick((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!employeeId) {
        setData([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      const res = await supabase
        .from('employee_benefits')
        .select(
          'enrollment_id, employee_id, plan_id, enrollment_status, enrollment_date, '
          + 'effective_date, termination_date, source_envelope_id',
        )
        .eq('employee_id', employeeId)
        .is('termination_date', null);
      if (cancelled) return;
      if (res.error) {
        setError(res.error.message);
        setData([]);
        setLoading(false);
        return;
      }
      const rows = (res.data ?? []) as unknown as BenefitElectionRow[];
      const planIds = Array.from(
        new Set(rows.map((r) => r.plan_id).filter((x): x is string => !!x)),
      );
      let planMap: Record<string, { plan_name: string | null; plan_type: string | null }> = {};
      if (planIds.length > 0) {
        const planRes = await supabase
          .from('benefits_plans')
          .select('plan_id, plan_name, plan_type')
          .in('plan_id', planIds);
        if (!planRes.error && planRes.data) {
          planMap = Object.fromEntries(
            (planRes.data as { plan_id: string; plan_name: string | null; plan_type: string | null }[])
              .map((p) => [p.plan_id, { plan_name: p.plan_name, plan_type: p.plan_type }]),
          );
        }
      }
      if (cancelled) return;
      setData(
        rows.map((r) => ({
          ...r,
          plan_name: r.plan_id ? planMap[r.plan_id]?.plan_name ?? null : null,
          plan_type: r.plan_id ? planMap[r.plan_id]?.plan_type ?? null : null,
        })),
      );
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [employeeId, tick]);

  return { data, loading, error, refetch };
}

// ---------------------------------------------------------------------------
// One employee's compensation history, newest period first.
// HR + superuser only by RLS; a non-HR reader gets an empty list, not an error.
// ---------------------------------------------------------------------------

export interface CompHistoryRow {
  comp_id: string;
  employee_id: string;
  effective_date: string;
  end_date: string | null;
  pay_type: string | null;
  wage_rate: number | null;
  pay_frequency: string | null;
  standard_hours: number | null;
  notes: string | null;
  created_by_eid: string | null;
}

export function useCompensationHistory(employeeId: string | undefined) {
  const [data, setData] = useState<CompHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const refetch = () => setTick((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!employeeId) {
        setData([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      const res = await supabase
        .from('employee_compensation')
        .select(
          'comp_id, employee_id, effective_date, end_date, pay_type, wage_rate, '
          + 'pay_frequency, standard_hours, notes, created_by_eid',
        )
        .eq('employee_id', employeeId)
        .order('effective_date', { ascending: false });
      if (cancelled) return;
      setData(res.error ? [] : ((res.data ?? []) as unknown as CompHistoryRow[]));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [employeeId, tick]);

  return { data, loading, refetch };
}

// ---------------------------------------------------------------------------
// The four audited HR write RPCs (migration 20260825223944).
//
// ⛔ Do NOT pass an actor from here. The functions derive the actor from the
// session with get_user_employee_id() and IGNORE a supplied one whenever a
// session exists — verified, not assumed. The p_actor_eid argument is there for
// callers with no session (a job, a backfill) and the browser is never one of
// those. Sending one would be a no-op that reads like a permission.
//
// Each RPC raises PT403/PT404/PT422/PT409 with a TCS_* hint; errorMessage()
// surfaces the message, which always names the offending value.
// ---------------------------------------------------------------------------

export interface SetCompensationInput {
  employee_id: string;
  effective_date: string;
  pay_type?: string | null;
  wage_rate?: number | null;
  pay_frequency?: string | null;
  standard_hours?: number | null;
  notes?: string | null;
}

export async function hrSetCompensation(input: SetCompensationInput) {
  const { data, error } = await supabase.rpc('hr_set_employee_compensation', {
    p_employee_id: input.employee_id,
    p_effective_date: input.effective_date,
    p_pay_type: input.pay_type ?? null,
    p_wage_rate: input.wage_rate ?? null,
    p_pay_frequency: input.pay_frequency ?? null,
    p_standard_hours: input.standard_hours ?? null,
    p_notes: input.notes ?? null,
  });
  if (error) throw error;
  return data;
}

export async function hrSetBenefitElection(input: {
  employee_id: string;
  plan_id: string;
  enrollment_status: 'pending' | 'elected' | 'waived' | 'not_eligible';
  effective_date: string;
  notes?: string | null;
}) {
  const { data, error } = await supabase.rpc('hr_set_benefit_election', {
    p_employee_id: input.employee_id,
    p_plan_id: input.plan_id,
    p_enrollment_status: input.enrollment_status,
    p_effective_date: input.effective_date,
    p_notes: input.notes ?? null,
  });
  if (error) throw error;
  return data;
}

export async function hrSetWorkState(
  employeeId: string,
  workState: string | null,
  source: 'primary_facility' | 'home_address' | 'manual' = 'manual',
) {
  const { data, error } = await supabase.rpc('hr_set_work_state', {
    p_employee_id: employeeId,
    p_work_state: workState,
    p_source: source,
  });
  if (error) throw error;
  return data;
}

export async function hrSetPayType(employeeId: string, payType: string | null) {
  const { data, error } = await supabase.rpc('hr_set_pay_type', {
    p_employee_id: employeeId,
    p_pay_type: payType,
  });
  if (error) throw error;
  return data;
}

// ---------------------------------------------------------------------------
// The benefits plans a card can elect into.
// ---------------------------------------------------------------------------
export interface BenefitsPlanRow {
  plan_id: string;
  plan_name: string | null;
  plan_type: string | null;
}

export function useBenefitsPlans() {
  const [data, setData] = useState<BenefitsPlanRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await supabase
        .from('benefits_plans')
        .select('plan_id, plan_name, plan_type')
        .order('plan_name', { ascending: true });
      if (cancelled) return;
      setData(res.error ? [] : ((res.data ?? []) as unknown as BenefitsPlanRow[]));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { data, loading };
}
